/**
 * MSW seed — synthetic Oxbo-M register + Oxbo-P plan store so the app runs
 * standalone. Grounded in the planner-seed demo (real AFE field schema; Vaca
 * Muerta unconventional + Golfo San Jorge conventional) and the W0 wireframe's
 * multi-event wells. Shapes are the SAME snake_case wire types the real
 * services return, so flipping VITE_USE_MOCKS=false is a drop-in swap.
 */
import type {
  WellOut,
  EventOut,
  PlanOut,
  SupplementOut,
  PlanDatasets,
  PlanTimelineOut,
  ResolvedCatalogOut,
} from "@api/types";

// ─────────────────────────── Oxbo-M register ────────────────────────────────

export const WELLS: WellOut[] = [
  {
    id: "w-fp1216",
    uwi: "AR.Nq.FP-1216",
    common_name: "Fortín de Piedra FP-1216 (h)",
    api14: null,
    basin: "Neuquina",
    field: "Fortín de Piedra",
    pad: "Pad FP-12",
    operator: "Tecpetrol",
    status: "planning",
    is_active: true,
  },
  {
    id: "w-fp1218",
    uwi: "AR.Nq.FP-1218",
    common_name: "Fortín de Piedra FP-1218 (h)",
    api14: null,
    basin: "Neuquina",
    field: "Fortín de Piedra",
    pad: "Pad FP-12",
    operator: "Tecpetrol",
    status: "planning",
    is_active: true,
  },
  {
    id: "w-bs041",
    uwi: "AR.Nq.BS-041",
    common_name: "Bandurria Sur BS-041 (h)",
    api14: null,
    basin: "Neuquina",
    field: "Bandurria Sur",
    pad: "Pad BS-04",
    operator: "YPF",
    status: "planning",
    is_active: true,
  },
  {
    id: "w-cs347",
    uwi: "AR.GSJ.CS-347",
    common_name: "Cañadón Seco CS-347",
    api14: null,
    basin: "Golfo San Jorge",
    field: "Cañadón Seco",
    pad: "CS-Norte",
    operator: "YPF",
    status: "planning",
    is_active: true,
  },
  {
    id: "w-cs351",
    uwi: "AR.GSJ.CS-351",
    common_name: "Cañadón Seco CS-351",
    api14: null,
    basin: "Golfo San Jorge",
    field: "Cañadón Seco",
    pad: "CS-Norte",
    operator: "YPF",
    status: "planning",
    is_active: true,
  },
  {
    id: "w-mb081",
    uwi: "AR.GSJ.MB-081",
    common_name: "Manantiales Behr MB-081",
    api14: null,
    basin: "Golfo San Jorge",
    field: "Manantiales Behr",
    pad: "MB-08",
    operator: "YPF",
    status: "planning",
    is_active: true,
  },
];

export const WELL_BY_ID: Record<string, WellOut> = Object.fromEntries(WELLS.map((w) => [w.id, w]));

// ─────────────────────────── Oxbo-P events (jobs) ───────────────────────────

export const EVENTS_BY_WELL_UWI: Record<string, EventOut[]> = {
  "AR.Nq.FP-1216": [
    {
      id: "ev-fp1216-pnc",
      well_uwi: "AR.Nq.FP-1216",
      event_code: "PNC",
      is_conventional: false,
      afe_no: "FPPT-01481-PE",
    },
    {
      id: "ev-fp1216-tfr",
      well_uwi: "AR.Nq.FP-1216",
      event_code: "TFR",
      is_conventional: false,
      afe_no: "FPPT-01490-TF",
    },
  ],
  "AR.Nq.FP-1218": [
    {
      id: "ev-fp1218-pnc",
      well_uwi: "AR.Nq.FP-1218",
      event_code: "PNC",
      is_conventional: false,
      afe_no: null,
    },
  ],
  "AR.Nq.BS-041": [
    {
      id: "ev-bs041-pnc",
      well_uwi: "AR.Nq.BS-041",
      event_code: "PNC",
      is_conventional: false,
      afe_no: "BSPT-00812-PE",
    },
    {
      id: "ev-bs041-tfr",
      well_uwi: "AR.Nq.BS-041",
      event_code: "TFR",
      is_conventional: false,
      afe_no: "BSPT-00819-TF",
    },
    {
      id: "ev-bs041-skl",
      well_uwi: "AR.Nq.BS-041",
      event_code: "SKL",
      is_conventional: false,
      afe_no: "BSPT-00825-SL",
    },
  ],
  "AR.GSJ.CS-347": [
    {
      id: "ev-cs347-per",
      well_uwi: "AR.GSJ.CS-347",
      event_code: "PER",
      is_conventional: true,
      afe_no: "FPPE-00347-PC",
    },
    {
      id: "ev-cs347-work",
      well_uwi: "AR.GSJ.CS-347",
      event_code: "WORK",
      is_conventional: true,
      afe_no: "FPPE-00352-WO",
    },
  ],
  "AR.GSJ.CS-351": [
    {
      id: "ev-cs351-per",
      well_uwi: "AR.GSJ.CS-351",
      event_code: "PER",
      is_conventional: true,
      afe_no: "FPPE-00361-PC",
    },
    {
      id: "ev-cs351-pull",
      well_uwi: "AR.GSJ.CS-351",
      event_code: "PULL",
      is_conventional: true,
      afe_no: "FPPE-00364-PL",
    },
    {
      id: "ev-cs351-work",
      well_uwi: "AR.GSJ.CS-351",
      event_code: "WORK",
      is_conventional: true,
      afe_no: null,
    },
  ],
  "AR.GSJ.MB-081": [
    {
      id: "ev-mb081-per",
      well_uwi: "AR.GSJ.MB-081",
      event_code: "PER",
      is_conventional: true,
      afe_no: "MBPE-00104-PC",
    },
    {
      id: "ev-mb081-pull",
      well_uwi: "AR.GSJ.MB-081",
      event_code: "PULL",
      is_conventional: true,
      afe_no: "MBPE-00109-PL",
    },
  ],
};

// ─────────────────────── Oxbo-P plans (per event) ───────────────────────────
// A plan row per event that already has a plan (status/version from the W0
// wireframe). Events with afe_no === null start with no plan (the New-plan flow).

export const PLANS_BY_EVENT: Record<string, PlanOut[]> = {
  // FP-1216 PNC carries a full append-only version history (W8 wireframe): v1/v2
  // locked, v3 approved (incorporates Supp 1), v4 the current draft. Newest-first
  // — `pl-fp1216-pnc` (the current v4, the one carrying the seeded datasets) is
  // the [0] the other screens' tests open against; MSW re-sorts desc anyway.
  "ev-fp1216-pnc": [
    plan("pl-fp1216-pnc", "ev-fp1216-pnc", "AR.Nq.FP-1216", "PNC", 4, "draft", "FPPT-01481-PE", 12420000, {
      actor: "R. Delgado (Ing. perforación)",
      updated_at: "2026-07-21T09:40:00Z",
      summary: "Ajuste de programa lateral: +2 fases de circulación tras revisión geomecánica; costo de fluidos revisado al alza.",
    }),
    plan("pl-fp1216-pnc-v3", "ev-fp1216-pnc", "AR.Nq.FP-1216", "PNC", 3, "approved", "FPPT-01481-PE", 12420000, {
      actor: "M. Suárez (Jefe de yacimiento)",
      updated_at: "2026-07-12T16:05:00Z",
      summary: "Incorpora Suplemento 1 (+US$ 620.000, curva direccional). Targets y casing sin cambios.",
    }),
    plan("pl-fp1216-pnc-v2", "ev-fp1216-pnc", "AR.Nq.FP-1216", "PNC", 2, "locked", "FPPT-01481-PE", 11800000, {
      actor: "R. Delgado (Ing. perforación)",
      updated_at: "2026-07-02T11:20:00Z",
      summary: 'Revisión de hole plan: sección lateral extendida de 2.300 m a 2.500 m; casing de producción actualizado a 5½".',
    }),
    plan("pl-fp1216-pnc-v1", "ev-fp1216-pnc", "AR.Nq.FP-1216", "PNC", 1, "locked", "FPPT-01481-PE", 11800000, {
      actor: "R. Delgado (Ing. perforación)",
      updated_at: "2026-06-18T08:15:00Z",
      summary: "Versión inicial del plan — AFE FPPT-01481-PE creada a partir de REQ-58021. Costo objetivo US$ 11,80 MM.",
    }),
  ],
  "ev-fp1216-tfr": [
    plan(
      "pl-fp1216-tfr",
      "ev-fp1216-tfr",
      "AR.Nq.FP-1216",
      "TFR",
      1,
      "draft",
      "FPPT-01490-TF",
      9450000,
    ),
  ],
  "ev-fp1218-pnc": [],
  "ev-bs041-pnc": [
    plan(
      "pl-bs041-pnc",
      "ev-bs041-pnc",
      "AR.Nq.BS-041",
      "PNC",
      2,
      "published",
      "BSPT-00812-PE",
      12400000,
    ),
  ],
  "ev-bs041-tfr": [
    plan(
      "pl-bs041-tfr",
      "ev-bs041-tfr",
      "AR.Nq.BS-041",
      "TFR",
      2,
      "draft",
      "BSPT-00819-TF",
      9900000,
    ),
  ],
  "ev-bs041-skl": [
    plan(
      "pl-bs041-skl",
      "ev-bs041-skl",
      "AR.Nq.BS-041",
      "SKL",
      1,
      "draft",
      "BSPT-00825-SL",
      4200000,
    ),
  ],
  "ev-cs347-per": [
    plan(
      "pl-cs347-per",
      "ev-cs347-per",
      "AR.GSJ.CS-347",
      "PER",
      2,
      "published",
      "FPPE-00347-PC",
      2260000,
    ),
  ],
  "ev-cs347-work": [
    plan(
      "pl-cs347-work",
      "ev-cs347-work",
      "AR.GSJ.CS-347",
      "WORK",
      1,
      "draft",
      "FPPE-00352-WO",
      395000,
    ),
  ],
  "ev-cs351-per": [
    plan(
      "pl-cs351-per",
      "ev-cs351-per",
      "AR.GSJ.CS-351",
      "PER",
      1,
      "published",
      "FPPE-00361-PC",
      2180000,
    ),
  ],
  "ev-cs351-pull": [
    plan(
      "pl-cs351-pull",
      "ev-cs351-pull",
      "AR.GSJ.CS-351",
      "PULL",
      1,
      "published",
      "FPPE-00364-PL",
      310000,
    ),
  ],
  "ev-cs351-work": [],
  "ev-mb081-per": [
    plan(
      "pl-mb081-per",
      "ev-mb081-per",
      "AR.GSJ.MB-081",
      "PER",
      1,
      "draft",
      "MBPE-00104-PC",
      2340000,
    ),
  ],
  "ev-mb081-pull": [
    plan(
      "pl-mb081-pull",
      "ev-mb081-pull",
      "AR.GSJ.MB-081",
      "PULL",
      3,
      "published",
      "MBPE-00109-PL",
      288000,
    ),
  ],
};

function plan(
  id: string,
  eventId: string,
  wellUwi: string,
  eventCode: string,
  version: number,
  status: PlanOut["status"],
  afe: string,
  total: number,
  extra?: { actor?: string; updated_at?: string; summary?: string; published_at?: string | null },
): PlanOut {
  return {
    id,
    event_id: eventId,
    well_uwi: wellUwi,
    event_code: eventCode,
    version,
    status,
    afe_no: afe,
    authorized_total: total,
    currency: "USD",
    actor: extra?.actor ?? "demo.planner",
    updated_at: extra?.updated_at ?? "2026-07-20T14:20:00Z",
    summary: extra?.summary ?? null,
    published_at:
      extra?.published_at ??
      (status === "published" ? "2026-07-19T09:00:00Z" : null),
  };
}

export const PLAN_BY_ID: Record<string, PlanOut> = Object.fromEntries(
  Object.values(PLANS_BY_EVENT)
    .flat()
    .map((p) => [p.id, p]),
);

// ─────────────────────── Plan design datasets (per plan) ─────────────────────
// Full datasets for two representative plans (unconventional drilling +
// conventional drilling); other plans get an empty-but-valid dataset skeleton.

export const DATASETS_BY_PLAN: Record<string, PlanDatasets> = {
  "pl-fp1216-pnc": {
    plan_id: "pl-fp1216-pnc",
    objetivo: [
      { seq: 1, name: "Formación objetivo", value: "Vaca Muerta (Cocina)", unit: null },
      { seq: 2, name: "Profundidad final (MD)", value: "5240", unit: "m" },
      { seq: 3, name: "Profundidad vertical (TVD)", value: "2680", unit: "m" },
      { seq: 4, name: "Longitud rama lateral", value: "2500", unit: "m" },
      { seq: 5, name: "Trayectoria", value: "Horizontal", unit: null },
    ],
    atributos: [
      { seq: 1, name: "Cuenca", value: "Neuquina", unit: null },
      { seq: 2, name: "Yacimiento", value: "Fortín de Piedra", unit: null },
      { seq: 3, name: "Tipo de pozo", value: "No convencional horizontal", unit: null },
      { seq: 4, name: "Rig", value: "DLS-4001", unit: null },
      { seq: 5, name: "Gradiente de fractura", value: "0.82", unit: "psi/ft" },
    ],
    programa: [
      {
        seq: 1,
        phase: "Guía",
        code_description: "Perforación guía + cementación",
        depth_from: 0,
        depth_until: 150,
        time_hours: 36,
        dol: 1.5,
        pct_completion: 5,
        amount_usd: 340000,
        time_hours_accumulated: 36,
      },
      {
        seq: 2,
        phase: "Superficie",
        code_description: 'Perforación superficie 12¼"',
        depth_from: 150,
        depth_until: 820,
        time_hours: 96,
        dol: 4,
        pct_completion: 18,
        amount_usd: 1120000,
        time_hours_accumulated: 132,
      },
      {
        seq: 3,
        phase: "Intermedia",
        code_description: 'Perforación intermedia 8½"',
        depth_from: 820,
        depth_until: 2740,
        time_hours: 168,
        dol: 7,
        pct_completion: 47,
        amount_usd: 3260000,
        time_hours_accumulated: 300,
      },
      {
        seq: 4,
        phase: "Curva",
        code_description: "Construcción de curva (build)",
        depth_from: 2740,
        depth_until: 3040,
        time_hours: 120,
        dol: 5,
        pct_completion: 63,
        amount_usd: 1980000,
        time_hours_accumulated: 420,
      },
      {
        seq: 5,
        phase: "Lateral",
        code_description: 'Navegación rama lateral 6"',
        depth_from: 3040,
        depth_until: 5240,
        time_hours: 216,
        dol: 9,
        pct_completion: 96,
        amount_usd: 3640000,
        time_hours_accumulated: 636,
      },
      {
        seq: 6,
        phase: "Terminación mecánica",
        code_description: "Bajada de casing + cementación producción",
        depth_from: 5240,
        depth_until: 5240,
        time_hours: 108,
        dol: 4.5,
        pct_completion: 100,
        amount_usd: 1460000,
        time_hours_accumulated: 744,
      },
    ],
    hole_plan: [
      {
        seq: 1,
        phase: "Guía",
        section: "Conductor",
        hole_diameter: 17.5,
        top_md: 0,
        base_md: 150,
        length: 150,
        dol: 1.5,
        target_cost: 340000,
        target_duration: 1.5,
      },
      {
        seq: 2,
        phase: "Superficie",
        section: "Superficie",
        hole_diameter: 12.25,
        top_md: 150,
        base_md: 820,
        length: 670,
        dol: 4,
        target_cost: 1120000,
        target_duration: 4,
      },
      {
        seq: 3,
        phase: "Intermedia",
        section: "Intermedia",
        hole_diameter: 8.5,
        top_md: 820,
        base_md: 2740,
        length: 1920,
        dol: 7,
        target_cost: 3260000,
        target_duration: 7,
      },
      {
        seq: 4,
        phase: "Curva",
        section: "Build",
        hole_diameter: 6,
        top_md: 2740,
        base_md: 3040,
        length: 300,
        dol: 5,
        target_cost: 1980000,
        target_duration: 5,
      },
      {
        seq: 5,
        phase: "Lateral",
        section: "Lateral",
        hole_diameter: 6,
        top_md: 3040,
        base_md: 5240,
        length: 2200,
        dol: 9,
        target_cost: 3640000,
        target_duration: 9,
      },
    ],
    casing: [
      {
        seq: 1,
        string_name: "Conductor",
        type: "Conductor",
        hole_size: 17.5,
        casing_size: 13.375,
        section_number: "1",
        top_installation: 0,
        base_installation_depth: 150,
        installation_length: 150,
      },
      {
        seq: 2,
        string_name: "Superficie",
        type: "Surface",
        hole_size: 12.25,
        casing_size: 9.625,
        section_number: "2",
        top_installation: 0,
        base_installation_depth: 820,
        installation_length: 820,
      },
      {
        seq: 3,
        string_name: "Producción",
        type: "Production",
        hole_size: 8.5,
        casing_size: 5.5,
        section_number: "3",
        top_installation: 0,
        base_installation_depth: 5240,
        installation_length: 5240,
      },
    ],
    afe_lines: [
      {
        seq: 1,
        account: "11-100",
        account_description: "Equipo de perforación (day rate)",
        phase: "Global",
        program_step: null,
        unit: "días",
        planned_qty: 1,
        rate: 3720000,
        budget: 3720000,
        accrual_method: "avance_etapas",
        material: "Servicio",
        code: "RIG",
      },
      {
        seq: 2,
        account: "11-210",
        account_description: "Trépanos y motores de fondo",
        phase: "Intermedia",
        program_step: 3,
        unit: "un",
        planned_qty: 6,
        rate: 160000,
        budget: 960000,
        accrual_method: "adjusted_cost",
        material: "Trépano PDC",
        code: "BIT",
      },
      {
        seq: 3,
        account: "11-320",
        account_description: "Fluidos de perforación",
        phase: "Lateral",
        program_step: 5,
        unit: "m³",
        planned_qty: 1,
        rate: 1480000,
        budget: 1480000,
        accrual_method: "devengado",
        material: "Lodo OBM",
        code: "MUD",
      },
      {
        seq: 4,
        account: "11-410",
        account_description: "Cañería de revestimiento",
        phase: "Terminación mecánica",
        program_step: 6,
        unit: "m",
        planned_qty: 5240,
        rate: 408.4,
        budget: 2140000,
        accrual_method: "devengado",
        material: "Casing 5½/9⅝",
        code: "CSG",
      },
      {
        seq: 5,
        account: "11-510",
        account_description: "Direccional / MWD-LWD",
        phase: "Curva",
        program_step: 4,
        unit: "servicio",
        planned_qty: 1,
        rate: 1620000,
        budget: 1620000,
        accrual_method: "avance_etapas",
        material: "Servicio direccional",
        code: "DIR",
      },
      {
        seq: 6,
        account: "11-900",
        account_description: "Logística, campamento e indirectos",
        phase: "Global",
        program_step: null,
        unit: "gl",
        planned_qty: 1,
        rate: 1000000,
        budget: 1000000,
        accrual_method: "straight_line",
        material: "Indirectos",
        code: "IND",
      },
    ],
    // Planned survey stations (server-computed columns as min-curvature would
    // return them): vertical hold to KOP 2,460 m, build to 90° over ~390 m at
    // 048°, then a ~2,340 m lateral. TVD/N-E/DLS mirror what putTrajectory rolls.
    trajectory: [
      { seq: 1, md: 0, inclination: 0, azimuth: 0, tvd: 0, northing: 0, easting: 0, dls: 0 },
      { seq: 2, md: 1000, inclination: 0, azimuth: 0, tvd: 1000, northing: 0, easting: 0, dls: 0 },
      { seq: 3, md: 2000, inclination: 0, azimuth: 0, tvd: 2000, northing: 0, easting: 0, dls: 0 },
      { seq: 4, md: 2460, inclination: 0, azimuth: 48, tvd: 2460, northing: 0, easting: 0, dls: 0 },
      {
        seq: 5,
        md: 2560,
        inclination: 20,
        azimuth: 48,
        tvd: 2558,
        northing: 11.6,
        easting: 12.8,
        dls: 6,
      },
      {
        seq: 6,
        md: 2660,
        inclination: 45,
        azimuth: 48,
        tvd: 2641.7,
        northing: 47.2,
        easting: 52.5,
        dls: 7.5,
      },
      {
        seq: 7,
        md: 2760,
        inclination: 70,
        azimuth: 48,
        tvd: 2695,
        northing: 103.2,
        easting: 114.6,
        dls: 7.5,
      },
      {
        seq: 8,
        md: 2850,
        inclination: 88,
        azimuth: 48,
        tvd: 2712.1,
        northing: 162.1,
        easting: 180,
        dls: 6,
      },
      {
        seq: 9,
        md: 2900,
        inclination: 90,
        azimuth: 48,
        tvd: 2712.9,
        northing: 195.5,
        easting: 217.2,
        dls: 1.2,
      },
      {
        seq: 10,
        md: 3900,
        inclination: 90,
        azimuth: 48,
        tvd: 2712.9,
        northing: 864.7,
        easting: 960.3,
        dls: 0,
      },
      {
        seq: 11,
        md: 4900,
        inclination: 90,
        azimuth: 48,
        tvd: 2712.9,
        northing: 1533.8,
        easting: 1703.5,
        dls: 0,
      },
      {
        seq: 12,
        md: 5240,
        inclination: 90,
        azimuth: 48,
        tvd: 2712.9,
        northing: 1761.3,
        easting: 1956.1,
        dls: 0,
      },
    ],
  },
  "pl-cs347-per": {
    plan_id: "pl-cs347-per",
    objetivo: [
      { seq: 1, name: "Formación objetivo", value: "Cañadón Seco (D-129)", unit: null },
      { seq: 2, name: "Profundidad final (MD)", value: "1980", unit: "m" },
      { seq: 3, name: "Trayectoria", value: "Vertical", unit: null },
    ],
    atributos: [
      { seq: 1, name: "Cuenca", value: "Golfo San Jorge", unit: null },
      { seq: 2, name: "Yacimiento", value: "Cañadón Seco", unit: null },
      { seq: 3, name: "Tipo de pozo", value: "Convencional vertical", unit: null },
      { seq: 4, name: "Rig", value: "AR-118", unit: null },
    ],
    programa: [
      {
        seq: 1,
        phase: "Superficie",
        code_description: 'Perforación superficie 12¼"',
        depth_from: 0,
        depth_until: 350,
        time_hours: 48,
        dol: 2,
        pct_completion: 22,
        amount_usd: 420000,
        time_hours_accumulated: 48,
      },
      {
        seq: 2,
        phase: "Producción",
        code_description: 'Perforación producción 8½"',
        depth_from: 350,
        depth_until: 1980,
        time_hours: 168,
        dol: 7,
        pct_completion: 78,
        amount_usd: 1280000,
        time_hours_accumulated: 216,
      },
      {
        seq: 3,
        phase: "Terminación mecánica",
        code_description: "Casing + cementación + perfilaje",
        depth_from: 1980,
        depth_until: 1980,
        time_hours: 96,
        dol: 4,
        pct_completion: 100,
        amount_usd: 560000,
        time_hours_accumulated: 312,
      },
    ],
    hole_plan: [
      {
        seq: 1,
        phase: "Superficie",
        section: "Superficie",
        hole_diameter: 12.25,
        top_md: 0,
        base_md: 350,
        length: 350,
        dol: 2,
        target_cost: 420000,
        target_duration: 2,
      },
      {
        seq: 2,
        phase: "Producción",
        section: "Producción",
        hole_diameter: 8.5,
        top_md: 350,
        base_md: 1980,
        length: 1630,
        dol: 7,
        target_cost: 1280000,
        target_duration: 7,
      },
    ],
    casing: [
      {
        seq: 1,
        string_name: "Superficie",
        type: "Surface",
        hole_size: 12.25,
        casing_size: 9.625,
        section_number: "1",
        top_installation: 0,
        base_installation_depth: 350,
        installation_length: 350,
      },
      {
        seq: 2,
        string_name: "Producción",
        type: "Production",
        hole_size: 8.5,
        casing_size: 7,
        section_number: "2",
        top_installation: 0,
        base_installation_depth: 1980,
        installation_length: 1980,
      },
    ],
    afe_lines: [
      {
        seq: 1,
        account: "11-100",
        account_description: "Equipo de perforación (day rate)",
        phase: "Global",
        program_step: null,
        unit: "días",
        planned_qty: 13,
        rate: 70000,
        budget: 910000,
        accrual_method: "avance_etapas",
        material: "Servicio",
        code: "RIG",
      },
      {
        seq: 2,
        account: "11-210",
        account_description: "Trépanos",
        phase: "Producción",
        program_step: 2,
        unit: "un",
        planned_qty: 2,
        rate: 90000,
        budget: 180000,
        accrual_method: "adjusted_cost",
        material: "Trépano tricónico",
        code: "BIT",
      },
      {
        seq: 3,
        account: "11-410",
        account_description: "Cañería de revestimiento",
        phase: "Terminación mecánica",
        program_step: 3,
        unit: "m",
        planned_qty: 1980,
        rate: 262.6,
        budget: 520000,
        accrual_method: "devengado",
        material: "Casing 7/9⅝",
        code: "CSG",
      },
      {
        seq: 4,
        account: "11-900",
        account_description: "Indirectos y logística",
        phase: "Global",
        program_step: null,
        unit: "gl",
        planned_qty: 1,
        rate: 200000,
        budget: 200000,
        accrual_method: "straight_line",
        material: "Indirectos",
        code: "IND",
      },
    ],
    trajectory: [
      { seq: 1, md: 0, inclination: 0, azimuth: 0, tvd: 0, northing: 0, easting: 0, dls: 0 },
      {
        seq: 2,
        md: 1980,
        inclination: 0.5,
        azimuth: 0,
        tvd: 1980,
        northing: 3,
        easting: 0,
        dls: 0.1,
      },
    ],
  },
};

// The prior version (v3) of the FP-1216 PNC plan — the diff baseline W8 compares
// v4 against. Derived from v4 (the seeded `pl-fp1216-pnc` dataset) with the two
// circulation phases removed and the pre-revision fluids budget, so the diff
// surfaces real field-level changes (phases 6→8, AFE total, fluids line).
{
  const v4 = DATASETS_BY_PLAN["pl-fp1216-pnc"];
  if (v4) {
    const v3 = structuredClone(v4);
    v3.plan_id = "pl-fp1216-pnc-v3";
    // v3 had one fewer phase before the geomech revision added a circulation step.
    v3.programa = v3.programa.filter((_, i) => i < v4.programa.length - 1);
    // Pre-revision drilling-fluids budget (11-320) before the upward revision.
    v3.afe_lines = v3.afe_lines.map((l) =>
      l.account === "11-320" ? { ...l, rate: 1200000, budget: 1200000 } : l,
    );
    DATASETS_BY_PLAN["pl-fp1216-pnc-v3"] = v3;
  }
}

/** An empty-but-valid dataset skeleton for plans without seeded design data. */
export function emptyDatasets(planId: string): PlanDatasets {
  return {
    plan_id: planId,
    objetivo: [],
    atributos: [],
    programa: [],
    hole_plan: [],
    casing: [],
    afe_lines: [],
    trajectory: [],
  };
}

// ─────────────────── Plan timeline (W7) — flat node contract ────────────────
// The Pad Fortín de Piedra multi-track schedule from the w7 wireframe: a CPE
// spine (rig DLS-4001 walking well-to-well + frac spread SET-FRAC-07), four
// well tracks (drill→complete→TPO), and shared services. Plan bars are editable;
// actual + NPT bars overlay read-only (as if fed back from Oxbo-Z).
export const TIMELINE_BY_PLAN: Record<string, PlanTimelineOut> = {
  "pl-fp1216-pnc": {
    plan_id: "pl-fp1216-pnc",
    site_id: "pad-fp12",
    today: "2026-09-18",
    horizon_start: "2026-08-04",
    horizon_end: "2026-11-03",
    lookahead_weeks: 4,
    tracks: [
      { id: "cpe-rig", kind: "cpe", label: "Rig · DLS-4001", sub: "drilling — walks well to well" },
      { id: "cpe-frac", kind: "cpe", label: "Frac spread · SET-FRAC-07", sub: "follows the rig" },
      { id: "well-fp1216", kind: "well", label: "FP-1216 (h)", sub: "drill → completion → TPO" },
      { id: "well-fp1218", kind: "well", label: "FP-1218 (h)", sub: "drill → completion → TPO" },
      { id: "well-fp1220", kind: "well", label: "FP-1220 (h)", sub: "next in sequence" },
      { id: "well-fp1222", kind: "well", label: "FP-1222 (h)", sub: "plan — not started" },
      { id: "svc-directional", kind: "service", label: "Directional / MWD", sub: null },
      { id: "svc-wireline", kind: "service", label: "Wireline (perforating)", sub: null },
      { id: "svc-ct", kind: "service", label: "Coiled tubing (TPO)", sub: null },
    ],
    nodes: [
      // CPE spine — rig walking (actual up to Today, plan beyond)
      { id: "n-rig-1216", track_id: "cpe-rig", track_kind: "cpe", kind: "actual", op: "drilling", label: "FP-1216 (h)", start: "2026-08-04", end: "2026-08-26", cpe_ref: "DLS-4001", seq: 1, depth_from: 0, depth_until: 5240, phase: "Drilling" },
      { id: "n-rig-xfer1", track_id: "cpe-rig", track_kind: "cpe", kind: "transfer", op: null, label: "move", start: "2026-08-26", end: "2026-08-29", cpe_ref: "DLS-4001", seq: 2, depth_from: null, depth_until: null, phase: null },
      { id: "n-rig-1218", track_id: "cpe-rig", track_kind: "cpe", kind: "actual", op: "drilling", label: "FP-1218 (h)", start: "2026-08-29", end: "2026-09-18", cpe_ref: "DLS-4001", seq: 3, depth_from: 0, depth_until: 5180, phase: "Drilling" },
      { id: "n-rig-1220", track_id: "cpe-rig", track_kind: "cpe", kind: "plan", op: "drilling", label: "FP-1220 (h) — plan", start: "2026-09-18", end: "2026-10-16", cpe_ref: "DLS-4001", seq: 4, depth_from: 0, depth_until: 5180, phase: "Drilling" },
      // Frac spread
      { id: "n-frac-1216", track_id: "cpe-frac", track_kind: "cpe", kind: "actual", op: "unconv", label: "FP-1216 · frac", start: "2026-09-01", end: "2026-09-16", cpe_ref: "SET-FRAC-07", seq: 1, depth_from: null, depth_until: null, phase: "Completion" },
      { id: "n-frac-1218", track_id: "cpe-frac", track_kind: "cpe", kind: "plan", op: "unconv", label: "FP-1218 · frac (plan)", start: "2026-09-18", end: "2026-10-04", cpe_ref: "SET-FRAC-07", seq: 2, depth_from: null, depth_until: null, phase: "Completion" },
      // FP-1216 well: drill (actual) → completion (actual) → TPO (plan) + milestones
      { id: "n-1216-drill", track_id: "well-fp1216", track_kind: "well", kind: "actual", op: "drilling", label: "Drilling", start: "2026-08-04", end: "2026-08-26", cpe_ref: "DLS-4001", seq: 1, depth_from: 0, depth_until: 5240, phase: "Drilling" },
      { id: "n-1216-comp", track_id: "well-fp1216", track_kind: "well", kind: "actual", op: "unconv", label: "Completion", start: "2026-09-01", end: "2026-09-16", cpe_ref: "SET-FRAC-07", seq: 2, depth_from: null, depth_until: null, phase: "Completion" },
      { id: "n-1216-tpo", track_id: "well-fp1216", track_kind: "well", kind: "plan", op: "ct", label: "TPO (plan)", start: "2026-09-18", end: "2026-09-28", cpe_ref: "CT-02", seq: 3, depth_from: null, depth_until: null, phase: "TPO" },
      { id: "n-1216-spud", track_id: "well-fp1216", track_kind: "well", kind: "milestone", op: "drilling", label: "spud", start: "2026-08-04", end: null, cpe_ref: null, seq: 0, depth_from: 0, depth_until: null, phase: null },
      { id: "n-1216-td", track_id: "well-fp1216", track_kind: "well", kind: "milestone", op: "drilling", label: "TD", start: "2026-08-26", end: null, cpe_ref: null, seq: 4, depth_from: 5240, depth_until: null, phase: null },
      // FP-1218 well: drill (actual, with NPT) → completion (plan)
      { id: "n-1218-drill-a", track_id: "well-fp1218", track_kind: "well", kind: "actual", op: "drilling", label: "Drilling", start: "2026-08-29", end: "2026-09-10", cpe_ref: "DLS-4001", seq: 1, depth_from: 0, depth_until: 2740, phase: "Drilling" },
      { id: "n-1218-npt", track_id: "well-fp1218", track_kind: "well", kind: "npt", op: "drilling", label: "NPT · stuck pipe", start: "2026-09-10", end: "2026-09-12", cpe_ref: "DLS-4001", seq: 2, depth_from: null, depth_until: null, phase: null },
      { id: "n-1218-drill-b", track_id: "well-fp1218", track_kind: "well", kind: "actual", op: "drilling", label: "Drilling", start: "2026-09-12", end: "2026-09-18", cpe_ref: "DLS-4001", seq: 3, depth_from: 2740, depth_until: 5180, phase: "Drilling" },
      { id: "n-1218-comp", track_id: "well-fp1218", track_kind: "well", kind: "plan", op: "unconv", label: "Completion (plan)", start: "2026-09-20", end: "2026-10-06", cpe_ref: "SET-FRAC-07", seq: 4, depth_from: null, depth_until: null, phase: "Completion" },
      // FP-1220 well: forecast plan only (spans look-ahead)
      { id: "n-1220-drill", track_id: "well-fp1220", track_kind: "well", kind: "plan", op: "drilling", label: "Drilling (plan)", start: "2026-09-18", end: "2026-10-16", cpe_ref: "DLS-4001", seq: 1, depth_from: 0, depth_until: 5180, phase: "Drilling" },
      { id: "n-1220-comp", track_id: "well-fp1220", track_kind: "well", kind: "plan", op: "unconv", label: "Completion (plan)", start: "2026-10-18", end: "2026-11-01", cpe_ref: "SET-FRAC-07", seq: 2, depth_from: null, depth_until: null, phase: "Completion" },
      // Shared services
      { id: "n-dir-1216", track_id: "svc-directional", track_kind: "service", kind: "actual", op: "drilling", label: "Dir · 1216", start: "2026-08-12", end: "2026-08-16", cpe_ref: null, seq: 1, depth_from: null, depth_until: null, phase: null },
      { id: "n-dir-1220", track_id: "svc-directional", track_kind: "service", kind: "plan", op: "drilling", label: "Dir · 1220 (plan)", start: "2026-09-24", end: "2026-09-28", cpe_ref: null, seq: 2, depth_from: null, depth_until: null, phase: null },
      { id: "n-wl-1216", track_id: "svc-wireline", track_kind: "service", kind: "actual", op: "wireline", label: "WL · 1216", start: "2026-09-02", end: "2026-09-04", cpe_ref: null, seq: 1, depth_from: null, depth_until: null, phase: null },
      { id: "n-ct-1216", track_id: "svc-ct", track_kind: "service", kind: "plan", op: "ct", label: "CT · 1216 (plan)", start: "2026-09-20", end: "2026-09-26", cpe_ref: "CT-02", seq: 1, depth_from: null, depth_until: null, phase: null },
    ],
  },
};

/** An empty-but-valid timeline for plans with no seeded schedule. */
export function emptyTimeline(planId: string): PlanTimelineOut {
  const today = new Date().toISOString().slice(0, 10);
  return {
    plan_id: planId,
    site_id: "unknown",
    today,
    horizon_start: today,
    horizon_end: today,
    lookahead_weeks: 4,
    tracks: [],
    nodes: [],
  };
}

// ─────────────────────── Supplements (D24) ──────────────────────────────────

export const SUPPLEMENTS_BY_PLAN: Record<string, SupplementOut[]> = {
  // FP-1216 PNC (the W8 demo plan): Supp 1 fully approved (counts toward the
  // authorized total), Supp 2 in flight with a multi-step approval trail.
  "pl-fp1216-pnc": [
    {
      id: "sup-fp1216-1",
      plan_id: "pl-fp1216-pnc",
      seq: 1,
      amount: 620000,
      reason: "Curva direccional — servicio MWD/LWD adicional por desvío de trayectoria en tramo build",
      status: "approved",
      actor: "R. Delgado",
      created_at: "2026-07-08T00:00:00Z",
      approvals: [
        { stage: "field", status: "approved", actor: "R. Delgado (Ing. perforación)", decided_at: "2026-07-08T14:20:00Z", note: "Desvío confirmado en sitio." },
        { stage: "office", status: "approved", actor: "M. Suárez (Jefe de yacimiento)", decided_at: "2026-07-09T10:05:00Z", note: null },
        { stage: "jv", status: "approved", actor: "Comité JV Fortín de Piedra", decided_at: "2026-07-11T16:00:00Z", note: null },
      ],
    },
    {
      id: "sup-fp1216-2",
      plan_id: "pl-fp1216-pnc",
      seq: 2,
      amount: 340000,
      reason: "Fluidos de perforación — cambio de sistema OBM por pérdida de circulación en intermedia",
      status: "requested",
      actor: "R. Delgado",
      created_at: "2026-07-19T00:00:00Z",
      approvals: [
        { stage: "field", status: "approved", actor: "R. Delgado (Ing. perforación)", decided_at: "2026-07-19T14:20:00Z", note: "Pérdida de circulación documentada en DDR-0089." },
        { stage: "office", status: "pending", actor: "M. Suárez (Jefe de yacimiento)", decided_at: null, note: null },
        { stage: "jv", status: "locked", actor: "Comité JV Fortín de Piedra", decided_at: null, note: null },
      ],
    },
  ],
  "pl-bs041-pnc": [
    {
      id: "sup-bs041-1",
      plan_id: "pl-bs041-pnc",
      seq: 1,
      amount: 600000,
      reason: "Sobrecosto de fluidos OBM",
      status: "approved",
      actor: "demo.planner",
      created_at: "2026-07-15T10:00:00Z",
    },
  ],
};

// ─────────────────────── Oxbo-M catalogs (pickers) ──────────────────────────

export const CATALOGS: Record<string, ResolvedCatalogOut> = {
  accounts: {
    category_key: "accounts",
    generation: "gen-1",
    entries: [
      { key: "11-100", label: "Equipo de perforación (day rate)", meta: { class: "servicio" } },
      { key: "11-210", label: "Trépanos y motores de fondo", meta: { class: "material" } },
      { key: "11-320", label: "Fluidos de perforación", meta: { class: "material" } },
      { key: "11-410", label: "Cañería de revestimiento", meta: { class: "material" } },
      { key: "11-510", label: "Direccional / MWD-LWD", meta: { class: "servicio" } },
      { key: "22-100", label: "Servicio de fractura (bombeo)", meta: { class: "servicio" } },
    ],
  },
  tubulars: {
    category_key: "tubulars",
    generation: "gen-1",
    entries: [
      { key: "csg-13375", label: 'Casing 13⅜"', meta: { od: 13.375 } },
      { key: "csg-9625", label: 'Casing 9⅝"', meta: { od: 9.625 } },
      { key: "csg-7", label: 'Casing 7"', meta: { od: 7 } },
      { key: "csg-55", label: 'Casing 5½"', meta: { od: 5.5 } },
    ],
  },
  formations: {
    category_key: "formations",
    generation: "gen-1",
    entries: [
      { key: "vm-cocina", label: "Vaca Muerta (Cocina)" },
      { key: "d129", label: "Cañadón Seco (D-129)" },
    ],
  },
  // Bit / hole-size catalog (Oxbo-M) — the W2 diameter picker binds to this so
  // the planner picks a standard hole size rather than inventing one. `meta.in`
  // is the diameter written to the hole-plan row.
  hole_sizes: {
    category_key: "hole_sizes",
    generation: "gen-1",
    entries: [
      { key: "hs-17500", label: '17½"', meta: { in: 17.5 } },
      { key: "hs-12250", label: '12¼"', meta: { in: 12.25 } },
      { key: "hs-8500", label: '8½"', meta: { in: 8.5 } },
      { key: "hs-6125", label: '6⅛"', meta: { in: 6.125 } },
      { key: "hs-6000", label: '6"', meta: { in: 6 } },
    ],
  },
};
